import { ButtonBase } from 'components/button';
import {
    GoogleAuthProvider,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
} from 'firebase/auth';
import { useRef, useState } from 'react';
import { auth } from 'firebaseStore';
import * as Sentry from '@sentry/react';

import './login.css';

const reset = (email: string, messageCallback: (_: string) => void) =>
    sendPasswordResetEmail(auth, email)
        .then(
            () => messageCallback('Email sent'),
            (args) => {
                console.warn('signIn reject args', args);
                if (`${args}`.startsWith('FirebaseError: Firebase: Error (auth/user-not-found).')) {
                    messageCallback('Email sent');
                    return Promise.resolve();
                } else if (
                    `${args}`.startsWith('FirebaseError: Firebase: Error (auth/invalid-email).')
                ) {
                    messageCallback('Invalid email');
                } else if (`${args}`.startsWith('FirebaseError: Firebase: Access to this')) {
                    messageCallback('Too many attempts, contact us');
                } else if (
                    `${args}`.startsWith('FirebaseError: Firebase: Error (auth/missing-email')
                ) {
                    messageCallback('an email address is needed');
                }
                return Promise.reject();
            }
        )
        .catch((error) => {
            console.error('sendPasswordResetEmail', error);
            return Promise.reject();
        });

const signInWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider).then((a) =>
        Sentry.setUser({ email: a.user.email || 'unknown@example.com' })
    );
};

const onLogin = (email: string, password: string, messageCallback: (_: string) => void) =>
    signInWithEmailAndPassword(auth, email, password)
        .then(
            (userCredential) => {
                Sentry.setUser({
                    email: userCredential.user.email || 'unknown@example.com',
                });
                return Promise.resolve();
            },
            (args) => {
                console.warn('signIn reject args', args);
                if (`${args}`.startsWith('FirebaseError: Firebase: Error (auth/wrong-password).')) {
                    messageCallback('Wrong email/password');
                } else if (
                    `${args}`.startsWith('FirebaseError: Firebase: Error (auth/user-not-found).')
                ) {
                    messageCallback('Wrong email/password');
                } else if (
                    `${args}`.startsWith('FirebaseError: Firebase: Error (auth/invalid-email).')
                ) {
                    messageCallback('Invalid email');
                }
                if (`${args}`.startsWith('FirebaseError: Firebase: Access to this')) {
                    messageCallback('Too many attempts, contact us');
                }
                return Promise.reject();
            }
        )
        .catch((error) => {
            console.error('login with password error', error);
            return Promise.reject();
        });

export const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [buttonText, setButtonText] = useState('Sign in');
    const [resetText, setResetText] = useState('Reset Password');
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: '80px',
                    border: '1px solid #1b1b1b08',
                    width: '453px',
                }}
            >
                <img
                    src="/logo.png"
                    style={{
                        height: '120px',
                        width: '100%',
                        borderRadius: '0px',
                        marginLeft: '-12px',
                        marginBottom: '68px',
                    }}
                    alt="Terminos logo"
                />
                <div style={{ marginLeft: '16px', fontSize: '18px', lineHeight: '27px' }}>
                    E-Mail Address
                </div>
                <input
                    ref={emailRef}
                    onClick={(e) => {
                        if (!emailRef) {
                            return;
                        }
                        if (!emailRef.current) {
                            return;
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        emailRef.current.focus();
                        emailRef.current.setSelectionRange(
                            emailRef.current.value.length,
                            emailRef.current.value.length
                        );
                    }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                        width: 'calc(100% - 5.5px)',
                        height: '42px',
                        border: '1px solid #C4C4C4',
                        boxShadow:
                            '0px 1px 1px rgba(0, 0, 0, 0.1), inset 0px 1px 1px rgba(0, 0, 0, 0.1)',
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onLogin(email, password, setButtonText);
                        }
                    }}
                />
                <div
                    style={{
                        marginLeft: '16px',
                        fontSize: '18px',
                        lineHeight: '27px',
                        marginTop: '9px',
                    }}
                >
                    Password
                </div>
                <input
                    ref={passwordRef}
                    type="password"
                    onClick={(e) => {
                        if (!passwordRef) {
                            return;
                        }
                        if (!passwordRef.current) {
                            return;
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        passwordRef.current.focus();
                        passwordRef.current.setSelectionRange(0, passwordRef.current.value.length);
                    }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                        width: 'calc(100% - 5.5px)',
                        height: '42px',
                        border: '1px solid #C4C4C4',
                        boxShadow:
                            '0px 1px 1px rgba(0, 0, 0, 0.1), inset 0px 1px 1px rgba(0, 0, 0, 0.1)',
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onLogin(email, password, setButtonText);
                        }
                    }}
                />
                <ButtonBase
                    className="loginButtonReset"
                    onClick={() =>
                        reset(email, (message) => {
                            setResetText(`Reset Password${message ? ' - ' : ''}${message || ''}`);
                        })
                    }
                    style={{
                        marginLeft: '12px',
                        padding: '4px',
                        fontSize: '18px',
                        lineHeight: '27px',
                        marginTop: '5px',
                        width: 'fit-content',
                    }}
                >
                    {resetText}
                </ButtonBase>
                <ButtonBase
                    className={`loginButton ${email && password ? 'signinButton' : 'disabled'}`}
                    style={{
                        marginTop: '14px',
                        width: '100%',
                        cursor: email && password ? 'pointer' : 'auto',
                    }}
                    onClick={
                        email && password
                            ? () => onLogin(email, password, setButtonText)
                            : undefined
                    }
                >
                    {buttonText}
                </ButtonBase>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '19px 0' }}>
                    Or
                </div>
                <ButtonBase
                    onClick={signInWithGoogle}
                    style={{ justifyContent: 'flex-start' }}
                    className="loginButton googleButton"
                >
                    <div
                        style={{
                            position: 'fixed',
                            backgroundColor: '#2E5DAA',
                            height: '46px',
                            width: '53px',
                            borderRadius: '3px 0 0 3px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <img
                            src="/Google.svg"
                            alt="Terminos logo"
                            style={{
                                height: '25px',
                                width: '25px',
                            }}
                        />
                    </div>
                    <div
                        style={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        Sign in with Google
                    </div>
                </ButtonBase>
            </div>
        </div>
    );
};

export const RegisterMe = () => {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: '80px',
                    border: '1px solid #1b1b1b08',
                    width: '453px',
                }}
            >
                <img
                    src="/logo.png"
                    style={{
                        height: '120px',
                        width: '100%',
                        borderRadius: '0px',
                        marginLeft: '-12px',
                        marginBottom: '68px',
                    }}
                    alt="Terminos logo"
                />
                <div style={{ marginLeft: '16px', fontSize: '18px', lineHeight: '27px' }}>
                    Please ask your organisation admin to add you to the account or contact sales at
                    <a href="mailto:roberto@terminos.io" style={{ marginLeft: '5px' }}>
                        roberto@terminos.io
                    </a>
                </div>
            </div>
        </div>
    );
};

export const ProfileLoading = () => {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
            }}
        >
            Loading your profile...
        </div>
    );
};
